import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { FdaDisclaimer, FtcDisclosure, InteractionBanner } from './Disclosures';

// Honest, goal-based supplement recommendations (SS3). The engine is deterministic
// and gated server-side — this just renders what it returns. Willing to say "food
// first" and "save your money"; every rec shows its evidence grade; medication /
// pregnancy flags surface as calm flag-and-defer notes. Used on /you (your goals)
// and /supplements (browse by goal).
//
// "Add to stack" is the bridge from a suggestion to Layer 2: it creates the
// regimen item with today as the start date, so the since-you-started read
// begins counting at once. Only for a signed-in person; the public quiz shows
// the suggestion without the button.

export const GOAL_LABELS = {
  sleep: 'Sleep', energy: 'Energy', immunity: 'Immunity', stress: 'Stress',
  muscle: 'Muscle', heart_brain: 'Heart & brain', digestion: 'Digestion',
  bone: 'Bone', general_longevity: 'Longevity',
};

const TIMING_LABELS = {
  morning: 'morning', with_food: 'with food', empty_stomach: 'empty stomach', evening: 'evening',
};

const doseLine = (r) => {
  const parts = [];
  if (r.default_dose != null) {
    parts.push(`${r.default_dose}${r.default_dose_unit ? ' ' + r.default_dose_unit : ''}`);
  }
  if (r.timing_hint && TIMING_LABELS[r.timing_hint]) parts.push(TIMING_LABELS[r.timing_hint]);
  return parts.join(' · ');
};

const todayIso = () => {
  // Local calendar day, not UTC: toISOString() rolls over at 00:00 UTC, which
  // on an evening in the Americas is tomorrow.
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Which catalog slugs are already in the signed-in person's stack. Shared by
// every list of suggestions on a page so the button can say "In your stack".
export const useStackSlugs = () => {
  const { user } = useAuth();
  const [slugs, setSlugs] = useState(null);
  const refresh = useCallback(async () => {
    if (!user) { setSlugs(null); return; }
    try {
      const res = await apiClient.get('/api/regimen', { params: { active_only: true } });
      setSlugs(new Set((res.data || []).map((i) => i.catalog_slug).filter(Boolean)));
    } catch {
      setSlugs(new Set());
    }
  }, [user]);
  useEffect(() => { refresh(); }, [refresh]);
  return { slugs, refresh, signedIn: !!user };
};

export const AddToStackButton = ({ r, stack }) => {
  const [busy, setBusy] = useState(false);
  if (!stack?.signedIn || !r.slug) return null;
  const inStack = stack.slugs?.has(r.slug);
  if (inStack) {
    return (
      <span className="inline-flex items-center rounded-full border border-line-strong px-[18px] py-2.5 text-base font-medium text-fg-2">
        In your stack
      </span>
    );
  }
  const add = async () => {
    setBusy(true);
    try {
      await apiClient.post('/api/regimen', {
        kind: 'supplement', catalog_slug: r.slug, name: r.name, started_at: todayIso(),
      });
      toast.success(`${r.name} added. We read what moves from today.`);
      stack.refresh?.();
    } catch {
      toast.error('Could not add that just now.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={add}
      disabled={busy}
      className="inline-flex items-center rounded-full bg-cta px-[18px] py-2.5 text-base font-medium text-cta-fg hover:bg-cta-hover disabled:opacity-50"
    >
      Add to stack
    </button>
  );
};

// One suggestion row: the name, a dot and two words for how strongly, one
// line of why, and the one button that matters.
const TAG = {
  recommend: { word: 'Worth it', dot: 'bg-good-strong dark:bg-good' },
  consider: { word: 'Worth a try', dot: 'bg-good-strong dark:bg-good' },
  food_first: { word: 'Food and habits first', dot: 'bg-fg-3' },
  skip: { word: 'Save your money', dot: 'bg-off-strong dark:bg-off' },
};

export const RecItem = ({ r, stack }) => {
  const [open, setOpen] = useState(false);
  const tag = TAG[r.strength] || TAG.consider;
  const canAdd = r.strength === 'recommend' || r.strength === 'consider';
  const why = r.rationale || r.food_first_note || '';
  return (
    <div className="grid grid-cols-1 items-center gap-3 px-5 py-4 sm:grid-cols-[1fr_auto] sm:gap-4">
      <div className="min-w-0">
        <div className="text-[1.1rem] font-medium">
          {r.name}
          <span className="ml-2.5 inline-flex items-center gap-2 text-sm font-normal text-fg-3">
            <i className={`inline-block h-2 w-2 rounded-full ${tag.dot}`} />{tag.word}
          </span>
        </div>
        {why && <p className="mt-1 text-sm text-fg-3">{why}</p>}
        {open && (
          <div className="mt-2 space-y-1 text-sm text-fg-3">
            {r.evidence_grade && <p>Evidence: {r.evidence_grade}.</p>}
            {r.food_first_note && r.rationale && <p>{r.food_first_note}</p>}
            {(doseLine(r) || r.price_hint) && <p>{doseLine(r) ? `Typical: ${doseLine(r)}` : ''}{doseLine(r) && r.price_hint ? ' · ' : ''}{r.price_hint ? `~${r.price_hint}` : ''}</p>}
            <InteractionBanner reasons={r.gate?.reasons} />
            {r.affiliate_url && (
              <a href={r.affiliate_url} target="_blank" rel="nofollow sponsored noopener noreferrer" className="inline-block font-medium text-fg-2 hover:text-fg">Where to buy →</a>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {canAdd && stack?.signedIn && r.slug ? <AddToStackButton r={r} stack={stack} /> : null}
        <button type="button" onClick={() => setOpen((o) => !o)}
          className="rounded-full border border-line-strong bg-transparent px-[18px] py-2.5 text-base font-medium text-fg-2 hover:text-fg">
          {open ? 'Less' : 'Why'}
        </button>
      </div>
    </div>
  );
};

const RecommendationsCard = ({ title = 'Supplement suggestions', showGoalPicker = true }) => {
  const [allGoals, setAllGoals] = useState([]);
  const [selected, setSelected] = useState([]);      // empty → server derives from profile
  const [recs, setRecs] = useState(null);
  const [activeGoals, setActiveGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const stack = useStackSlugs();

  useEffect(() => {
    apiClient.get('/api/recommendations/goals')
      .then((res) => setAllGoals(res.data?.goals || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = selected.length ? { goals: selected.join(',') } : {};
      const res = await apiClient.get('/api/recommendations', { params });
      setRecs(res.data?.recommendations || []);
      setActiveGoals(res.data?.goals || []);
    } catch {
      setRecs([]);
    } finally {
      setLoading(false);
    }
  }, [selected]);

  useEffect(() => { load(); }, [load]);

  const toggleGoal = (g) =>
    setSelected((s) => (s.includes(g) ? s.filter((x) => x !== g) : [...s, g]));

  const hasAffiliate = (recs || []).some((r) => r.affiliate_url);

  return (
    <div className="rounded-2xl border border-line bg-raised p-5 space-y-4">
      <div>
        <h2 className="text-lg font-medium text-fg">{title}</h2>
        <p className="text-sm text-fg-2">
          Evidence-graded, and honest about when food does the job or you can skip it.
        </p>
      </div>

      {showGoalPicker && allGoals.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {allGoals.map((g) => {
            const on = selected.includes(g);
            return (
              <button
                key={g}
                type="button"
                onClick={() => toggleGoal(g)}
                className={`text-sm px-2.5 py-1 rounded-full border transition-colors ${
                  on
                    ? 'bg-fg border-fg text-canvas'
                    : 'border-line text-fg-2 hover:bg-elev'
                }`}
              >
                {GOAL_LABELS[g] || g}
              </button>
            );
          })}
        </div>
      )}

      {!showGoalPicker && activeGoals.length > 0 && (
        <p className="text-sm text-fg-3">
          For your goal: {activeGoals.map((g) => GOAL_LABELS[g] || g).join(', ')}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-fg-3">Loading…</p>
      ) : (recs || []).length === 0 ? (
        <p className="text-sm text-fg-2">
          Nothing to suggest here — and that’s often the honest answer. Food and
          habits carry most of the weight.
        </p>
      ) : (
        <div className="divide-y divide-line rounded-xl border border-line">
          {recs.map((r, i) => <RecItem key={`${r.goal}-${r.slug}-${i}`} r={r} stack={stack} />)}
        </div>
      )}

      <div className="pt-2 border-t border-line space-y-2">
        <FtcDisclosure hasAffiliate={hasAffiliate} />
        <FdaDisclaimer />
      </div>
    </div>
  );
};

export default RecommendationsCard;
